import { FC, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AppButton, FigmaTypography } from "@libs/zapi-ui/custom";
import { getRoutePromptComparison, getRoutePromptTestCases } from "../../shared/const/router";
import {
  MAX_COMPARISONS_PER_BATCH,
  createComparisonBatch,
  formatInputCount,
  listRevisionComparisons,
  listTestCases,
  type RevisionComparisonSummary,
  type TestCaseListItem,
  type TestVerdict
} from "../../shared/services/promptTests";

const VERDICT_STYLES: Record<TestVerdict, string> = {
  pending: "bg-gray-100 text-gray-500",
  pass: "bg-green-100 text-green-800",
  fail: "bg-red-100 text-red-800",
  inconclusive: "bg-amber-100 text-amber-800"
};

const POLL_INTERVAL_MS = 3000;

function isComparisonRunning(comparison: RevisionComparisonSummary): boolean {
  return [comparison.baselineRunStatus, comparison.candidateRunStatus].some(
    (status) => status === "pending" || status === "running"
  );
}

// Run status is about whether the replay completed, which reads as a pass/fail if shown next to the
// verdict. Only surface it when it needs action.
function describeRuns(comparison: RevisionComparisonSummary): string {
  if (isComparisonRunning(comparison)) return "Running…";
  if ([comparison.baselineRunStatus, comparison.candidateRunStatus].some((status) => status === "failed")) {
    return "Could not finish";
  }
  return new Date(comparison.createdAt).toLocaleDateString();
}

type Props = {
  featureKey: string;
  revisionId: number;
  isActive: boolean;
  passedTestCaseCount: number;
  requiredPassedTestCaseCount: number;
  onVerdictsChanged: () => void;
};

export const RevisionTests: FC<Props> = ({
  featureKey,
  revisionId,
  isActive,
  passedTestCaseCount,
  requiredPassedTestCaseCount,
  onVerdictsChanged
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedVersionIds, setSelectedVersionIds] = useState<number[]>([]);

  const { data: testCases = [] } = useQuery<TestCaseListItem[]>({
    queryKey: ["prompt-test-cases"],
    queryFn: listTestCases
  });

  const { data: comparisons = [] } = useQuery<RevisionComparisonSummary[]>({
    queryKey: ["prompt-revision-comparisons", revisionId],
    queryFn: () => listRevisionComparisons(revisionId),
    refetchInterval: (query) => {
      const rows = query.state.data as RevisionComparisonSummary[] | undefined;
      return rows?.some(isComparisonRunning) ? POLL_INTERVAL_MS : false;
    }
  });

  // The activate gate lives on the revision detail query, so refresh it once a batch settles.
  const runningCount = comparisons.filter(isComparisonRunning).length;
  const previousRunningCount = useRef(runningCount);
  useEffect(() => {
    if (previousRunningCount.current > 0 && runningCount === 0) onVerdictsChanged();
    previousRunningCount.current = runningCount;
  }, [runningCount, onVerdictsChanged]);

  const runnable = testCases.filter((tc) => tc.latestVersionId !== null);

  const toggle = (versionId: number) => {
    setSelectedVersionIds((current) => {
      if (current.includes(versionId)) return current.filter((id) => id !== versionId);
      return current.length >= MAX_COMPARISONS_PER_BATCH ? current : [...current, versionId];
    });
  };

  const run = useMutation({
    mutationFn: (testCaseVersionIds: number[]) =>
      createComparisonBatch({ testCaseVersionIds, candidateRevisionId: revisionId }),
    onSuccess: (data) => {
      setSelectedVersionIds([]);
      queryClient.invalidateQueries({ queryKey: ["prompt-revision-comparisons", revisionId] });

      if (data.comparisonIds.length === 1) {
        navigate(getRoutePromptComparison(featureKey, revisionId, data.comparisonIds[0]));
        return;
      }
      toast.success(`${data.comparisonIds.length} comparisons started`);
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      toast.error(error?.response?.data?.message ?? "Failed to start the comparisons")
  });

  const selectableCount = Math.min(runnable.length, MAX_COMPARISONS_PER_BATCH);
  const allSelected = selectableCount > 0 && selectedVersionIds.length === selectableCount;
  const atCap = selectedVersionIds.length >= MAX_COMPARISONS_PER_BATCH;

  return (
    <div className="rounded-lg border bg-card px-6 py-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <FigmaTypography component="div" variant="body-2" className="text-gray-900">
            Tests
          </FigmaTypography>
          {!isActive && requiredPassedTestCaseCount > 0 && (
            <span
              className={`rounded px-2 py-0.5 text-[12px] leading-[18px] ${
                passedTestCaseCount >= requiredPassedTestCaseCount
                  ? "bg-green-100 text-green-800"
                  : "bg-gray-100 text-gray-600"
              }`}
            >
              {passedTestCaseCount} of {requiredPassedTestCaseCount} test cases passed
            </span>
          )}
        </div>
        <Link
          to={getRoutePromptTestCases(featureKey, revisionId)}
          className="text-[12px] text-gray-500 hover:text-gray-700"
        >
          Manage test cases →
        </Link>
      </div>

      {isActive ? (
        <FigmaTypography component="div" variant="body-3" className="text-gray-500 mt-2">
          This is the active revision — it is the baseline every comparison runs against, so there is nothing to compare
          it to.
        </FigmaTypography>
      ) : runnable.length === 0 ? (
        <FigmaTypography component="div" variant="body-3" className="text-gray-500 mt-2">
          No test cases yet.{" "}
          <Link to={getRoutePromptTestCases(featureKey, revisionId)} className="underline">
            Capture one from a case brief
          </Link>{" "}
          to run a before/after comparison.
        </FigmaTypography>
      ) : (
        <>
          <div className="mt-3 flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() =>
                setSelectedVersionIds(
                  allSelected
                    ? []
                    : runnable.slice(0, MAX_COMPARISONS_PER_BATCH).map((tc) => tc.latestVersionId as number)
                )
              }
              className="text-[12px] text-gray-500 hover:text-gray-700"
            >
              {allSelected ? "Clear selection" : `Select ${selectableCount < runnable.length ? "first " : ""}${selectableCount}`}
            </button>
            <AppButton
              onClick={() => run.mutate(selectedVersionIds)}
              disabled={selectedVersionIds.length === 0}
              isLoading={run.isPending}
              loadingText="Starting..."
            >
              Run {selectedVersionIds.length || ""} comparison{selectedVersionIds.length === 1 ? "" : "s"}
            </AppButton>
          </div>

          <div className="mt-2 max-h-[220px] divide-y overflow-y-auto rounded border border-gray-200">
            {runnable.map((tc) => {
              const checked = selectedVersionIds.includes(tc.latestVersionId as number);
              const blocked = atCap && !checked;

              return (
                <label
                  key={tc.id}
                  className={`flex items-center gap-2 px-3 py-2 ${
                    blocked ? "cursor-not-allowed opacity-40" : "cursor-pointer hover:bg-gray-50"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={blocked}
                    onChange={() => toggle(tc.latestVersionId as number)}
                    className="h-4 w-4"
                  />
                  <span className="truncate text-[13px] text-gray-800">{tc.name}</span>
                  <span className="ml-auto shrink-0 text-[12px] text-gray-400">
                    v{tc.latestVersion} · {formatInputCount(tc.turnCount)}
                  </span>
                </label>
              );
            })}
          </div>
        </>
      )}

      {!isActive && (
        <FigmaTypography component="div" variant="body-3" className="text-gray-400 mt-2">
          Each comparison runs the full conversation twice on the strong model — it uses real tokens and takes a few
          minutes. Up to three run at once, {MAX_COMPARISONS_PER_BATCH} per batch.
        </FigmaTypography>
      )}

      {comparisons.length > 0 && (
        <div className="mt-4 divide-y border-t">
          {comparisons.map((c) => (
            <Link
              key={c.id}
              to={getRoutePromptComparison(featureKey, revisionId, c.id)}
              className="flex items-center justify-between gap-3 py-2 hover:bg-gray-50"
            >
              <div className="min-w-0">
                <div className="truncate text-[13px] text-gray-800">
                  {c.testCaseName} · v{c.testCaseVersion}
                </div>
                <div className="text-[12px] text-gray-400">{describeRuns(c)}</div>
              </div>
              <span className={`shrink-0 rounded px-2 py-0.5 text-[12px] leading-[18px] ${VERDICT_STYLES[c.verdict]}`}>
                {c.verdict}
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};
